import { z } from "zod";
import {
  LanguageEnumSchema,
  OrgStatusEnumValue,
  UserRoleEnumValue,
  ProgramRoleEnumValue,
  PortfolioRoleEnumValue,
  ZodErrorMessageEnumValue,
} from "./enums.js";

export enum OrgListOfNonWorkingDaysEnum {
  MON = "MON",
  TUE = "TUE",
  WED = "WED",
  THU = "THU",
  FRI = "FRI",
  SAT = "SAT",
  SUN = "SUN",
}
export enum OrgListOfRiskTypeEnum {
  TECHNIQUE = "Technique",
  HUMAIN = "Humain",
  JURIDIQUE = "Juridique",
  FINANCIER = "Financier",

}
const disposableEmailDomains = [
  "mailinator.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  // "yopmail.com",
];
export const organisationIdSchema = z.string().uuid();

export const createOrganisationSchema = z.object({
  organisationName: z.string().min(1),
  industry: z.string().min(1),
  status: z.nativeEnum(OrgStatusEnumValue),
  country: z.string().min(1),
  phoneNumber: z.string({ required_error: "Please insert a valid phone number in organisation details" }).min(1, "Please insert a valid phone number in organisation details").max(20, "Phone number is limited to 20 digits"),
  countryCode: z.string().min(1).optional(),
  nonWorkingDays: z.nativeEnum(OrgListOfNonWorkingDaysEnum).array().optional(),
  createDemoProjects: z.boolean().optional(),
});

const severityTypeSchema = z.array(
  z.object({
    severityId: z.string().uuid().optional(),
    min: z.number().int(),
    max: z.number().int(),
    type: z.string().min(1),
  })
);

/** Stored on each rule: all eligible roles, or an explicit subset (same string values as default-line visible roles). */
export const TIMESHEET_EMAIL_RECIPIENT_ROLES = [
  UserRoleEnumValue.ADMINISTRATOR,
  UserRoleEnumValue.PROJECT_MANAGER,
  ProgramRoleEnumValue.PROGRAM_MANAGER,
  PortfolioRoleEnumValue.PORTFOLIO_MANAGER,
  UserRoleEnumValue.TEAM_MEMBER,
] as const;

export const timesheetEmailRecipientRoleSchema = z.enum(TIMESHEET_EMAIL_RECIPIENT_ROLES);

export const timesheetEmailRecipientsSchema = z.union([
  z.literal("ALL"),
  z.array(timesheetEmailRecipientRoleSchema).min(1),
]);

export const timesheetEmailNotificationRuleSchema = z.object({
  dayOfWeek: z.nativeEnum(OrgListOfNonWorkingDaysEnum),
  hour: z.number().int().min(0).max(23),
  minute: z.number().int().min(0).max(59),
  recipients: timesheetEmailRecipientsSchema,
  enabled: z.boolean(),
});

export const timesheetEmailNotificationsSchema = z.object({
  generated: timesheetEmailNotificationRuleSchema,
  reminder1: timesheetEmailNotificationRuleSchema,
  reminder2: timesheetEmailNotificationRuleSchema,
});

export type TimesheetEmailNotifications = z.infer<typeof timesheetEmailNotificationsSchema>;
export type TimesheetEmailRecipientRole = z.infer<typeof timesheetEmailRecipientRoleSchema>;

export const updateOrganisationSchema = z.object({
  organisationName: z.string({ required_error: ZodErrorMessageEnumValue.REQUIRED }).min(1),
  industry: z.string().min(1).optional(),
  status: z.nativeEnum(OrgStatusEnumValue).optional(),
  country: z.string().min(1).optional(),
  nonWorkingDays: z.nativeEnum(OrgListOfNonWorkingDaysEnum).array().optional(),
  jobTitlesOfOrg: z.string().array().optional(),
  riskType: z.string().array().optional(),
  phoneNumber: z.string({
    required_error: "Please insert a valid phone number in organisation details"
  })
    .min(1, "Please insert a valid phone number in organisation details")
    .max(20, "Phone number is limited to 20 digits"),
  countryCode: z.string().min(1).optional(),
  userId: z.string().uuid(),
  severityTypes: severityTypeSchema.optional(),
  onTimeDelayMax: z.number().int().min(1).max(99).optional(),
  moderateDelayMax: z.number().int().min(2).max(100).optional(),
  onBudgetMax: z.number().int().min(1).max(99).optional(),
  moderateBudgetMax: z.number().int().min(2).max(100).optional(),
  timesheetMaxHoursPerDay: z.number().int().min(1).max(24).optional(),
  timesheetEmailNotifications: timesheetEmailNotificationsSchema.optional(),
});


export const addOrganisationMemberSchema = z.object({
  email: z
    .string({ required_error: ZodErrorMessageEnumValue.REQUIRED })
    .email({ message: "Email is not valid" })
    .refine(
      (email) => {
        const domain = email.split("@")[1];
        return !disposableEmailDomains.includes(domain ?? "");
      },
      {
        message: "Please use a permanent email address to sign up",
      }
    ),
  language: z.nativeEnum(LanguageEnumSchema).optional(),
  role: z.nativeEnum(UserRoleEnumValue).refine(
    (value) => {
      return (
        value in UserRoleEnumValue &&
        (value === UserRoleEnumValue.TEAM_MEMBER ||
          value === UserRoleEnumValue.PROJECT_MANAGER ||
          value === UserRoleEnumValue.ADMINISTRATOR)
      );
    },
    {
      message: "Only team member, project manager and administrator roles allowed",
    }
  ),
  isAdmin: z.boolean(),
  projectId: z.string().uuid().optional(),
});

export const organisationStatuSchema = z.object({
  status: z.nativeEnum(OrgStatusEnumValue),
});

export const memberRoleSchema = z.object({
  role: z.nativeEnum(UserRoleEnumValue).refine(
    (value) => {
      return (
        value in UserRoleEnumValue &&
        (value === UserRoleEnumValue.PROJECT_MANAGER ||
          value === UserRoleEnumValue.TEAM_MEMBER ||
          value === UserRoleEnumValue.ADMINISTRATOR)
      );
    },
    {
      message: "Only team member, project manager and administrator roles are allowed",
    }
  ),
});

export const reAssginedTaskSchema = z.object({
  oldUserId: z.string().uuid().array(),
  newUserId: z.string().uuid(),
});

export const assignProjectAndRoleToUserSchema = z.object({
  isRoleChangePmToTm: z.boolean(),
  arrayOfRoleAndProjectId: z
    .object({
      projectRoleForUser: z.nativeEnum(UserRoleEnumValue),
      projectId: z.string().uuid(),
    })
    .array(),
  isCollaborator: z.boolean().default(false),
});

export const addMemberToOrgSchema = z.object({
  email: z
    .string({ required_error: ZodErrorMessageEnumValue.REQUIRED })
    .email({ message: "Email is not valid" })
    .refine(
      (email) => {
        const domain = email.split("@")[1];
        return !disposableEmailDomains.includes(domain ?? "");
      },
      {
        message: "Please use a permanent email address to sign up",
      }
    ),
  language: z.nativeEnum(LanguageEnumSchema).optional(),
  isAdmin: z.boolean(),
  isCollaborator: z.boolean().default(false),
});

export const organisationUserBlockUnblockSchema = z.object({
  organisationId: z.string().uuid(),
  userOrganisationId: z.string().uuid(),
});

export const updateOrganisationMemberSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: z.nativeEnum(UserRoleEnumValue).optional(),
  teamUnitId: z.string().nullable().optional(),
  orgUnitId: z.string().nullable().optional(),
  jobTitleId: z.string().nullable().optional(),
  contractTypeId: z.string().nullable().optional(),
  resourceType: z.enum(["INTERNAL", "EXTERNAL"]).nullable().optional(),
  contractualCapacity: z.number().int().min(0).max(100).optional(),
  dailyCapacity: z.number().min(0).max(24).optional(),
  managerId: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
});

export const roleChangePmToTmSchema = z.object({
  projectId: z.string().uuid(),
  newProjectManagerUserId: z.string().uuid(),
});

